import React, { useState } from 'react';

import { FormComponent, UiComponent } from 'components';
import { FileServiceQuery, StickerQuery } from 'queries';
import useModal from 'hooks/useModal';
import useFileService from 'hooks/useFileService';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { ISticker } from 'types';
import { stickerUploadDirectory } from '../constants';
import * as FormStyle from './style';

interface Props {
  sticker: ISticker;
}

type FormValueType = Pick<
  ISticker,
  'name' | 'acquisitionGuide' | 'description'
>;

function Form({ sticker }: Props) {
  const { handleClose, handleAlert } = useModal();
  const { mutateAsync: update } = StickerQuery.usePut();
  const { handleUploadd: uploadImage } = FileServiceQuery.useUpload(
    stickerUploadDirectory(sticker.id, 'main'),
  );
  const { handleUploadd: uploadLockedImage } = FileServiceQuery.useUpload(
    stickerUploadDirectory(sticker.id, 'locked'),
  );
  const [isSaving, setIsSaving] = useState(false);

  const image = useFileService({ initFileUrl: sticker.imageUrl });
  const lockedImage = useFileService({
    initFileUrl: sticker.lockedImageUrl ?? '',
  });

  const submit = async (data: FormValueType) => {
    if (isSaving) return;
    if (!data.name?.trim()) {
      handleAlert('이름을 입력해주세요');
      return;
    }

    setIsSaving(true);
    try {
      let imageUrl = sticker.imageUrl;
      if (image.file) {
        const res = await uploadImage(image.file);
        if (!res) return;
        imageUrl = res.data.url;
      }

      let lockedImageUrl = sticker.lockedImageUrl ?? undefined;
      if (lockedImage.file) {
        const res = await uploadLockedImage(lockedImage.file);
        if (!res) return;
        lockedImageUrl = res.data.url;
      }

      await update({
        id: sticker.id,
        name: data.name,
        acquisitionGuide: data.acquisitionGuide,
        description: data.description,
        imageUrl,
        lockedImageUrl,
      });
      toast.success('스티커가 수정되었습니다.');
      handleClose();
    } catch {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormComponent.Form css={FormStyle.wrap} submit={submit}>
      <div css={FormStyle.readOnlyRow}>
        <div>
          <UiComponent.FilterTitle title="코드" />
          <p css={FormStyle.readOnlyValue}>{sticker.code}</p>
        </div>
        <div>
          <UiComponent.FilterTitle title="활성 여부" />
          <p css={FormStyle.readOnlyValue}>
            {sticker.isActive ? '활성' : '비활성'}
          </p>
        </div>
      </div>

      <UiComponent.FilterTitle title="이름" required />
      <FormComponent.Input name="name" defaultValue={sticker.name} />

      <UiComponent.FilterTitle title="획득 방법" />
      <FormComponent.Input
        name="acquisitionGuide"
        defaultValue={sticker.acquisitionGuide}
      />

      <UiComponent.FilterTitle title="설명" />
      <FormComponent.Textarea
        name="description"
        defaultValue={sticker.description}
        css={{ height: 90 }}
      />

      <div css={FormStyle.imageRow}>
        <div>
          <UiComponent.FilterTitle title="실제 이미지" />
          <UiComponent.FileUpload
            name="imageUrl"
            fileUrl={image.fileUrl}
            handleFile={image.handleFile}
          />
        </div>
        <div>
          <UiComponent.FilterTitle title="잠금 이미지" />
          <UiComponent.FileUpload
            name="lockedImageUrl"
            fileUrl={lockedImage.fileUrl}
            handleFile={lockedImage.handleFile}
          />
        </div>
      </div>

      <div css={FormStyle.footer}>
        <UiComponent.Button
          variant="border"
          color="primary"
          onClick={handleClose}
        >
          취소
        </UiComponent.Button>
        <UiComponent.Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSaving}
        >
          저장하기
        </UiComponent.Button>
      </div>
    </FormComponent.Form>
  );
}

export default Form;
